import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from 'react';
import classNames from 'classnames';
import Icon from '@icon/Icon';
import { Dropdown } from '@widget/popups/Dropdown';
import { hasValue } from '@utils';
const DEFAULT_ICONS = {
    error: 'fas circle-exclamation',
    warning: 'fas triangle-exclamation',
    info: 'fas circle-info',
};
// The caller's `mode` is the ceiling; the measure loop only ever steps down this chain.
const NEXT_DOWN = { default: 'inline', inline: 'indicator' };
/**
 * @export
 * Themed error/warning state with three levels of prominence.
 *
 * `mode` is a **maximum**, not a fixed layout: the component measures its container
 * and degrades to fit — `default` → `inline` → `indicator` — so a card dropped into
 * a small widget tile becomes a row, then an icon. It never upgrades, and while the
 * container size is still unknown it renders the requested mode.
 *
 * Renders without a UXP context — pass every string in and it is safe to use from
 * error boundaries and other places where the provider may be missing.
 *
 * @example Full-page card
 * ```tsx
 * <ErrorState
 *   variant="warning"
 *   code="404"
 *   title="Page not found"
 *   description="The page you are looking for doesn't exist."
 *   action={<Button title="Go back" onClick={goBack} />}
 * />
 * ```
 *
 * @example Inline notice inside a gated tab
 * ```tsx
 * <ErrorState
 *   mode="inline"
 *   icon={['fas', 'lock']}
 *   title="Access Denied"
 *   description="You are not authorized to view this section"
 *   descriptionDisplay="inline"
 * />
 * ```
 *
 * @example Icon-only indicator with the detail in a dropdown
 * ```tsx
 * <ErrorState
 *   mode="indicator"
 *   title="Sync failed"
 *   description="The last sync attempt failed 3 minutes ago."
 *   action={<Button title="Retry" onClick={retry} />}
 * />
 * ```
 *
 * @category Feedback
 */
const ErrorState = (props) => {
    const { title, description, summary, icon, code, variant = 'error', mode = 'default', descriptionDisplay = 'dropdown', action, className, styles, } = props;
    const rootRef = React.useRef(null);
    const [effectiveMode, setEffectiveMode] = React.useState(mode);
    const [, bump] = React.useReducer((n) => n + 1, 0);
    // The caller moved the ceiling — start again from it.
    React.useLayoutEffect(() => setEffectiveMode(mode), [mode]);
    // Post-commit verify, the way useOverflowLayout does it: render, measure the real
    // box against the container, and step down if it overflows. Pre-paint, so the user
    // never sees the oversized pass. Each step strictly shrinks the content and there are
    // only three ranks, so this settles in at most two passes.
    React.useLayoutEffect(() => {
        const root = rootRef.current;
        const container = root === null || root === void 0 ? void 0 : root.parentElement;
        const next = NEXT_DOWN[effectiveMode];
        if (!root || !container || !next)
            return;
        const containerWidth = container.clientWidth;
        const containerHeight = container.clientHeight;
        if (!containerWidth)
            return; // not laid out yet — nothing to judge
        // An auto-height container grows with us and can never be "overflowed" vertically.
        const tooTall = containerHeight > 0 && root.scrollHeight > containerHeight + 1;
        const tooWide = root.scrollWidth > containerWidth + 1;
        if (tooTall || tooWide)
            setEffectiveMode(next);
    });
    // The container changed size: growth may fit the ceiling again, so restart from it;
    // shrinkage just needs the verify pass re-run. Only growth resets — after a step down
    // an auto-height container shrinks with the content, and treating that as a reset
    // would loop forever.
    React.useLayoutEffect(() => {
        var _a;
        const container = (_a = rootRef.current) === null || _a === void 0 ? void 0 : _a.parentElement;
        if (!container)
            return;
        let last = { width: container.clientWidth, height: container.clientHeight };
        const observer = new ResizeObserver(() => {
            const width = container.clientWidth;
            const height = container.clientHeight;
            const grew = width > last.width || height > last.height;
            last = { width, height };
            if (grew)
                setEffectiveMode(mode);
            else
                bump();
        });
        observer.observe(container);
        return () => observer.disconnect();
    }, [mode]);
    const rootClasses = classNames('uxpcore_errorstate', `uxpcore_errorstate--${effectiveMode}`, `uxpcore_errorstate--${variant}`, 
    // a full card that shrank to fit stays centred in its parent, as the card was
    { 'uxpcore_errorstate--fitted': mode === 'default' && effectiveMode !== 'default' }, className);
    const badge = (_jsx("div", { className: "uxpcore_errorstate__icon", children: _jsx(Icon, { icon: (icon !== null && icon !== void 0 ? icon : DEFAULT_ICONS[variant]) }) }));
    const renderTitle = () => hasValue(title)
        ? _jsx("div", { className: "uxpcore_errorstate__title", children: title })
        : null;
    const renderDescription = () => description
        ? _jsx("div", { className: "uxpcore_errorstate__description", children: description })
        : null;
    const renderAction = () => action
        ? _jsx("div", { className: "uxpcore_errorstate__action", children: action })
        : null;
    if (effectiveMode === 'indicator') {
        return (_jsx("div", { className: rootClasses, style: styles, ref: rootRef, children: _jsx(Dropdown, { position: "bottom-left", trigger: badge, content: _jsxs("div", { className: "uxpcore_errorstate__popup", children: [renderTitle(), renderDescription(), renderAction()] }) }) }));
    }
    if (effectiveMode === 'inline') {
        // Own element so the badge/title spacing never depends on Dropdown's trigger markup.
        const row = (_jsxs("div", { className: "uxpcore_errorstate__row", children: [badge, _jsxs("div", { className: "uxpcore_errorstate__message", children: [renderTitle(), descriptionDisplay === 'inline'
                            ? renderDescription()
                            : hasValue(summary) ? _jsx("div", { className: "uxpcore_errorstate__description", children: summary }) : null] })] }));
        return (_jsxs("div", { className: rootClasses, style: styles, ref: rootRef, children: [descriptionDisplay === 'dropdown' && description
                    ? _jsx(Dropdown, { position: "bottom-left", trigger: row, content: _jsx("div", { className: "uxpcore_errorstate__popup", children: renderDescription() }) })
                    : row, renderAction()] }));
    }
    return (_jsx("div", { className: rootClasses, style: styles, ref: rootRef, children: _jsxs("div", { className: "uxpcore_errorstate__panel", children: [hasValue(code)
                    ? _jsx("div", { className: "uxpcore_errorstate__code", children: code })
                    : badge, _jsxs("div", { className: "uxpcore_errorstate__message", children: [renderTitle(), renderDescription()] }), renderAction()] }) }));
};
export default ErrorState;
